// src/Context/AchievementToastContext.tsx
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { getAccessToken } from "../Components/Utils/TokenRefresh";

export interface AchievementUnlock {
  id: string;
  name: string;
  description?: string;
  rewardType: "coins" | "moonrocks";
  rewardAmount: number;
  /** merge | producer | quiz | creature | economy | roleplay | daily */
  category?: string;
  /** Both rewards when an achievement pays coins and moonrocks. */
  rewards?: { coins: number; moonrocks: number };
}

interface AchievementToastContextType {
  /** Call after key actions (quiz complete, merge, etc.) to check for new unlocks */
  checkAchievements: () => Promise<void>;
  /** Manually push an achievement toast (for testing or server-push) */
  pushAchievement: (achievement: AchievementUnlock) => void;
  /** The queue of achievements waiting to be shown */
  queue: AchievementUnlock[];
  /** The currently visible toast (first in queue) */
  current: AchievementUnlock | null;
  /** Dismiss the current toast */
  dismiss: () => void;
}

const AchievementToastContext = createContext<AchievementToastContextType | null>(null);

export const useAchievementToast = () => {
  const ctx = useContext(AchievementToastContext);
  if (!ctx) throw new Error("useAchievementToast must be used within AchievementToastProvider");
  return ctx;
};

export const AchievementToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<AchievementUnlock[]>([]);
  // Track which achievement IDs we've already shown this session to avoid duplicates
  const shownRef = useRef<Set<string>>(new Set());
  // Prevent concurrent /check calls
  const checkingRef = useRef(false);

  const current = queue.length > 0 ? queue[0] : null;

  const dismiss = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const pushAchievement = useCallback((achievement: AchievementUnlock) => {
    if (shownRef.current.has(achievement.id)) return;
    shownRef.current.add(achievement.id);
    setQueue((prev) => [...prev, achievement]);
  }, []);

  const checkAchievements = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;

    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/achievements/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newUnlocks: AchievementUnlock[] = res.data?.newUnlocks || res.data?.newlyUnlocked || [];
      for (const unlock of newUnlocks) {
        pushAchievement({
          id: unlock.id || unlock.name,
          name: unlock.name,
          description: unlock.description || "",
          rewardType: unlock.rewardType || "coins",
          rewardAmount: unlock.rewardAmount || 0,
          category: unlock.category || "",
          rewards: unlock.rewards,
        });
      }
    } catch (err) {
      // Silently fail, achievement toasts are non-critical
      console.warn("Achievement check failed:", err);
    } finally {
      checkingRef.current = false;
    }
  }, [pushAchievement]);

  // Development only: lets screenshots and manual QA show a celebration on demand.
  React.useEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined;
    (window as any).__mrPushAchievement = (a: AchievementUnlock) => {
      shownRef.current.delete(a.id);
      pushAchievement(a);
    };
    return () => {
      delete (window as any).__mrPushAchievement;
    };
  }, [pushAchievement]);

  return (
    <AchievementToastContext.Provider
      value={{ checkAchievements, pushAchievement, queue, current, dismiss }}
    >
      {children}
    </AchievementToastContext.Provider>
  );
};
